import useCurrentUser from "@casino/legacy/client/hooks/user/useCurrentUser";
import useSite from "@casino/legacy/client/hooks/useSite";
import { useEffect } from "react";
import internationalCallingCodes from "../InternationalCallingCodes";

type FormValuesWithCountryCode = {
  countryCode?: string;
};

type FormNameOptions<T extends FormValuesWithCountryCode> = keyof T;

type Props<T extends FormValuesWithCountryCode> = {
  autofillFromGeo?: boolean;
  value: string | number;
  onChange: (name: FormNameOptions<T>, value: string) => void;
};

const phonesSorter = (prev: SelectOption, next: SelectOption) => {
  if (next.title) {
    if (!prev.title || (prev.title && next.title > prev.title)) {
      return -1;
    }
    return 1;
  }
  const prevValue = prev?.title?.toLowerCase() || "";
  const nextValue = next?.title?.toLowerCase() || "";
  if (prevValue > nextValue) {
    return 1;
  }
  return -1;
};

const getInternationalCallingCodes = (primary: boolean, site: Site) => {
  const keys = Object.keys(internationalCallingCodes);
  const list = keys.reduce((acc: SelectOption[], key: string) => {
    const item = internationalCallingCodes[key];
    const primarySites = item.primary;
    const isValidCallingCode = primary
      ? primarySites && primarySites.includes(site)
      : !primarySites || (primarySites && !primarySites.includes(site));

    if (isValidCallingCode) {
      return [
        ...acc,
        { label: `${key} (+${item.code})`, value: item.value, title: key },
      ];
    }

    return acc;
  }, []);

  if (!list) {
    return [];
  }

  return list.sort(phonesSorter);
};

const useCountryCodeSelect = <T extends FormValuesWithCountryCode>({
  autofillFromGeo,
  value,
  onChange,
}: Props<T>) => {
  const { countryInfo } = useCurrentUser();
  const { site } = useSite();
  useEffect(() => {
    if (autofillFromGeo && countryInfo?.countryCode && !value) {
      const callingCountry =
        internationalCallingCodes[countryInfo?.countryCode];
      if (callingCountry) {
        onChange("countryCode", callingCountry.value);
      }
    }
  }, [autofillFromGeo, countryInfo?.countryCode, onChange, value]);

  const primary: SelectOption[] = getInternationalCallingCodes(true, site);
  const secondary: SelectOption[] = getInternationalCallingCodes(false, site);

  return { primary, secondary };
};

export default useCountryCodeSelect;
